<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { CONFIGURATION } from '@/enum';
import Checkbox from 'primevue/checkbox';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

import { storeToRefs } from 'pinia';
import { useMessageCenterStore } from '@/store';
import type { AttachmentFileConfig, FileTypes } from '@/types';

const messageCenterStore = useMessageCenterStore();
const { configuration, getFileTypes, getFileConfigurations, error } = storeToRefs(messageCenterStore);

onMounted(async () => {
  await init();
});

const maxFiles = ref<number>(1);
const maxUploadSize = ref<number>(10);
const fileTypes = ref<FileTypes[]>([]);
const hasError = computed(() => !!error.value);

const init = async () => {
  await messageCenterStore.getMessageSettings();
  setupConfiguration();
};

const setupConfiguration = async () => {
  const cfg = getFileConfigurations.value;
  maxFiles.value = typeof cfg?.max_files === 'number' && cfg.max_files > 0 ? cfg.max_files : 1;
  maxUploadSize.value = typeof cfg?.max_upload_size === 'number' && cfg.max_upload_size > 0 ? cfg.max_upload_size : 10;

  fileTypes.value = configuration.value?.file_types ?? [];
};

const saveConfiguration = async () => {
  const newConfiguration: AttachmentFileConfig = {
    configurations: [
      {
        name: CONFIGURATION.MAX_FILES,
        value: maxFiles.value,
      },
      {
        name: CONFIGURATION.MAX_UPLOAD_SIZE,
        value: maxUploadSize.value,
      },
    ],
    file_types: fileTypes.value,
  };

  await messageCenterStore.updateMessageSettings(newConfiguration);
  await setupConfiguration();

  if (hasError.value) {
    const message = error.value.data.errors
      ? Object.values(error.value.data.errors).flat().join(', ')
      : error.value.data.message;

    messageCenterStore.doToast(message, 'Error', true, 5000);
    messageCenterStore.error = undefined;
  } else {
    messageCenterStore.doToast('Configuration saved successfully.', 'Success');
  }
};
const showInformation = (ext: string | undefined): boolean => {
  if (!ext) return false;
  const showInfoByExt = ['webm', 'ogg', 'm4a'];
  return showInfoByExt.includes(ext);
};
</script>

<template>
  <Card>
    <template #content>
      <div class="flex">
        <div class="mb-2 w-full">
          <div class="flex items-center justify-between mb-2">
            <h2 class="text-lg font-semibold text-gray-800">Max Files</h2>
          </div>
          <div class="flex items-center mb-4">
            <label for="maxFiles" class="text-sm font-medium text-gray-700 col-8">
              Set the maximum number of files allowed for upload.
            </label>

            <input
              id="maxFiles"
              v-model.number="maxFiles"
              type="number"
              class="p-inputtext p-component px-3 py-2 text-sm border border-gray-300 rounded-md w-1/3 col-4"
              min="1"
              max="10"
            />
          </div>
        </div>
      </div>

      <div class="flex">
        <div class="mb-2 w-full">
          <div class="flex items-center justify-between mb-2">
            <h2 class="text-lg font-semibold text-gray-800">Max Upload Size</h2>
          </div>
          <div class="flex items-center mb-4">
            <label for="maxUploadSize" class="text-sm font-medium text-gray-700 col-8">
              Set the maximum file size allowed for uploads (in megabytes).
            </label>
            <input
              id="maxUploadSize"
              v-model.number="maxUploadSize"
              type="number"
              class="p-inputtext p-component px-3 py-2 text-sm border border-gray-300 rounded-md w-1/3 col-4"
              min="1"
              max="1024"
            />
          </div>
        </div>
      </div>

      <div class="flex justify-between items-center mb-2">
        <h2 class="text-lg font-semibold">Allowed File Types</h2>
      </div>
      <div class="flex items-center">
        <span class="text-sm font-medium text-gray-700 col-8">Manage allowed file types for different categories.</span>
      </div>

      <DataTable
        :value="fileTypes"
        :rows="fileTypes.length || 1"
        responsive-layout="scroll"
        sort-field="category"
        row-group-mode="rowspan"
        group-rows-by="category"
        :row-hover="true"
      >
        <template #empty>No documents found</template>

        <Column field="category" header="Category">
          <template #body="slotProps">
            <span class="font-bold text-gray-600" v-text="slotProps.data.category" />
          </template>
        </Column>

        <Column field="mime_type" header="MIME type">
          <template #body="slotProps">
            <span>{{ slotProps.data.mime_type }}</span>
            <Icon
              v-if="showInformation(slotProps.data.ext)"
              v-tooltip="slotProps.data.description"
              name="info"
              class="ml-3 text-gray-500 text-s"
            />
          </template>
        </Column>

        <Column field="icon" header="Icon">
          <template #body="slotProps">
            <span v-text="slotProps.data.icon" />
          </template>
        </Column>

        <Column field="ext" header="Extension">
          <template #body="slotProps">
            <span v-text="slotProps.data.ext" />
          </template>
        </Column>

        <Column header="Allowed">
          <template #body="slotProps">
            <Checkbox v-model="slotProps.data.allowed" :binary="true" />
          </template>
        </Column>

        <template #groupheader="slotProps">
          <span v-text="slotProps.data.category" />
        </template>
      </DataTable>

      <div class="flex items-center justify-between mt-4">
        <div class="col-9">
          <strong>Currently allowed extensions:&nbsp;</strong>
          <code>{{ getFileTypes.map((type) => type.ext).join(', ') }}</code>
        </div>

        <div class="col-3 flex justify-end">
          <AppButton text="Save" type="primary" style="margin-right: 0.5rem" @click="saveConfiguration" />
          <AppButton text="Cancel" type="secondary" />
        </div>
      </div>
    </template>
  </Card>
</template>

<style lang="scss" scoped>
.input-errors {
  color: $errorColor;
}

:deep(.p-card-body) {
  padding-top: 1rem !important;
}
</style>
